import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { sendTrackEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { Button, ProductTour } from '@openedx/paragon';
import { Translate, Lock } from '@openedx/paragon/icons';
import { stringifyUrl } from 'query-string';

import { registerOverrideMethod } from '@src/generic/plugin-store';

import messages from './messages';
import TranslationModal from './translation-modal';
import useTranslationTour from './useTranslationTour';
import useSelectLanguage from './useSelectLanguage';
import useUnitTitle from './useUnitTitle';
import FeedbackWidget from '../feedback-widget';
import './index.scss';

const TranslationSelection = ({
  id,
  isLegacyButton,
  courseId,
  courseLanguage,
  availableLanguages,
  unitId,
  unitTitle,
  isAuditMode,
  isFBE,
  verifiedMode,
  logEventProperties,
  dispatch,
}) => {
  const { formatMessage } = useIntl();
  const { userId } = getAuthenticatedUser();
  const {
    translationTour, isOpen, open, close,
  } = useTranslationTour();

  const { selectedLanguage, setSelectedLanguage } = useSelectLanguage({
    courseId,
    courseLanguage,
    availableLanguages,
  });

  const logButtonProperties = { ...logEventProperties, is_audit_user: isAuditMode };
  const buttonIcon = isAuditMode ? Lock : Translate;
  const ariaDescription = isAuditMode ? 'Upgrade to unlock course translations' : 'Select language to translate content into';

  const handleTranslateClick = () => {
    sendTrackEvent('edx.whole_course_translations.translate_button.clicked', logButtonProperties);
    open();
  };

  useUnitTitle({
    courseId,
    selectedLanguage,
    unitId,
    unitTitle,
    courseLanguage,
  });

  useEffect(() => {
    if ((courseId && selectedLanguage && unitId && userId) && (courseLanguage !== selectedLanguage) && !isAuditMode) {
      const eventName = 'edx.whole_course_translations.translation_requested';
      const eventProperties = {
        courseId,
        sourceLanguage: courseLanguage,
        targetLanguage: selectedLanguage,
        unitId,
        userId,
      };
      sendTrackEvent(eventName, eventProperties);
    }
  }, [courseId, courseLanguage, selectedLanguage, unitId, userId]);

  useEffect(() => {
    sendTrackEvent('edx.whole_course_translations.translate_button', logButtonProperties);
  }, [courseId]);

  useEffect(() => {
    dispatch(
      registerOverrideMethod({
        pluginName: id,
        methodName: 'getIFrameUrl',
        method: (iframeUrl) => {
          const finalUrl = stringifyUrl({
            url: iframeUrl,
            query: {
              ...((courseLanguage && selectedLanguage && (courseLanguage !== selectedLanguage)) && {
                src_lang: courseLanguage,
                dest_lang: selectedLanguage,
              }),
            },
          });
          return finalUrl;
        },
      }),
    );
  }, [courseLanguage, selectedLanguage]);

  return (
    <div className={isLegacyButton ? '' : 'toolbar-divider'}>
      <ProductTour tours={[translationTour]} />
      <Button
        iconBefore={buttonIcon}
        aria-description={ariaDescription}
        alt="change-language"
        variant={isLegacyButton ? 'outline-primary' : 'tertiary'}
        className={isLegacyButton ? 'translation-selection-button' : 'text-primary-500 icon-hover'}
        onClick={handleTranslateClick}
        size={!isLegacyButton && 'sm'}
      >
        {formatMessage(messages.translationButtonText)}
      </Button>
      <TranslationModal
        {...{
          availableLanguages,
          close,
          courseId,
          id,
          isOpen,
          isAuditMode,
          isFBE,
          selectedLanguage,
          setSelectedLanguage,
          verifiedMode,
          logEventProperties,
          originalCourseLanguage: courseLanguage,
        }}
      />
      <FeedbackWidget
        courseId={courseId}
        courseLanguage={courseLanguage}
        translationLanguage={selectedLanguage}
        unitId={unitId}
        userId={userId}
      />
    </div>
  );
};

TranslationSelection.propTypes = {
  dispatch: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  isLegacyButton: PropTypes.bool.isRequired,
  courseId: PropTypes.string.isRequired,
  unitId: PropTypes.string.isRequired,
  courseLanguage: PropTypes.string.isRequired,
  availableLanguages: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  unitTitle: PropTypes.string.isRequired,
  isFBE: PropTypes.bool.isRequired,
  verifiedMode: PropTypes.shape({
    currencySymbol: PropTypes.string,
    price: PropTypes.number,
    upgradeUrl: PropTypes.string,
  }).isRequired,
  isAuditMode: PropTypes.bool.isRequired,
  logEventProperties: PropTypes.shape({
    org_key: PropTypes.string.isRequired,
    courserun_key: PropTypes.string.isRequired,
    course_name: PropTypes.string.isRequired,
  }).isRequired,
};

TranslationSelection.defaultProps = {};

export default TranslationSelection;
